import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { runJgsActivitySweep, type JgsSweepCounters } from "@/services/adminDashboard";

const COUNTER_LABELS: Array<{ key: keyof JgsSweepCounters; label: string }> = [
  { key: "profilesScanned", label: "Profiles scanned" },
  { key: "baselined", label: "Baselined" },
  { key: "unchanged", label: "Unchanged" },
  { key: "immaterial", label: "Immaterial" },
  { key: "changed", label: "Changed" },
  { key: "rolledOver", label: "Rolled over" },
  { key: "skipped", label: "Skipped" },
  { key: "errors", label: "Errors" },
];

const JgsSweepCard = () => {
  const { toast } = useToast();
  const [running, setRunning] = useState(false);
  const [counters, setCounters] = useState<JgsSweepCounters | null>(null);

  async function handleRunSweep() {
    try {
      setRunning(true);
      const result = await runJgsActivitySweep();
      setCounters(result);
      toast({
        title: "JGS activity sweep complete",
        description: `Scanned ${result.profilesScanned} profiles — ${result.changed} changed, ${result.errors} errors`,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "JGS activity sweep failed", description: message, variant: "destructive" });
    } finally {
      setRunning(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>JGS Activity Sweep</CardTitle>
            <CardDescription>
              Run the daily JGS change-detection sweep on demand. This can take a while.
            </CardDescription>
          </div>
          <Button variant="default" size="sm" onClick={handleRunSweep} disabled={running}>
            {running ? "Running…" : "Run JGS Activity Sweep"}
          </Button>
        </div>
      </CardHeader>
      {counters ? (
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            {COUNTER_LABELS.map(({ key, label }) => (
              <div key={key} className="space-y-1">
                <div className="text-sm text-muted-foreground">{label}</div>
                <div className="font-medium">{counters[key]}</div>
              </div>
            ))}
          </div>
        </CardContent>
      ) : null}
    </Card>
  );
};

export default JgsSweepCard;
